type ClerkErrorEntry = {
	code?: string;
	message?: string;
	longMessage?: string;
};

type ClerkError = {
	errors: ClerkErrorEntry[];
	message?: string;
};

const isClerkError = (error: unknown): error is ClerkError => {
	return (
		typeof error === "object" &&
		error !== null &&
		"errors" in error &&
		Array.isArray((error as ClerkError).errors)
	);
};

const getPrimaryClerkError = (error: unknown): ClerkErrorEntry | undefined => {
	return isClerkError(error) ? error.errors[0] : undefined;
};

export const isDuplicateUserError = (error: unknown): boolean => {
	return (
		isClerkError(error) && error.errors[0]?.code === "form_identifier_exists"
	);
};

export const isIncorrectPasscodeError = (error: unknown): boolean => {
	return isClerkError(error) && error.errors[0]?.code === "form_code_incorrect";
};

export const isTestModeEmail = (email: string) => {
	return email.endsWith("+clerk_test@memorang.com");
};

export const formatError = (error: unknown): string => {
	const clerkError = getPrimaryClerkError(error);
	if (clerkError) {
		return clerkError.longMessage || clerkError.message || "Unknown error";
	}

	if (error instanceof Error && error.message) {
		return error.message;
	}

	if (typeof error === "string" && error.length > 0) {
		return error;
	}

	return "Unknown error";
};

export const isSignUpError = (error: unknown): boolean => {
	return typeof error === "object" && error !== null && "isSignUp" in error;
};

export const isAuthenticationInvalidError = (error: unknown): boolean => {
	return (
		isClerkError(error) && error.errors[0]?.code === "authentication_invalid"
	);
};

const NON_ACTIONABLE_AUTH_MESSAGE_PATTERNS = [
	"signed out",
	"authentication invalid",
	"no sign up/sign in was found with id",
] as const;

export const isNonActionableAuthError = (error: unknown): boolean => {
	if (isAuthenticationInvalidError(error)) {
		return true;
	}

	const message = formatError(error).toLowerCase();
	return NON_ACTIONABLE_AUTH_MESSAGE_PATTERNS.some((pattern) =>
		message.includes(pattern),
	);
};
